import { ActionReducerMapBuilder, PayloadAction, createAction } from '@reduxjs/toolkit'
import { FileUploadState } from 'components/ComposerMediaContext'
import { Draft } from 'immer'
import { Photo } from 'interfaces'
import { PartialObserver } from 'rxjs'
import { sortByKeyAscending } from 'shared'
import { StoreThunkDispatch, createAppAsyncThunk } from 'store/state'
import { getMediaDimensions, getVideoDuration } from 'utils/file/helpers'
import ComposerState, { ComposerVideoState, MediaItemState } from '../state'
import { V1 } from './net'

export const mediaReducers = {
  setComposerBulkUploadFileUrl: (
    state: Draft<ComposerState>,
    action: PayloadAction<{ url: string | undefined, name?: string, rows?: number }>
  ) => {
    state.bulkUploadFile = action.payload.url ? {
      url: action.payload.url,
      rows: action.payload.rows || 0,
      name: action.payload.name || ''
    } : undefined
  },
  videoFileAdded: (state: Draft<ComposerState>, action: PayloadAction<File>) => {
    const fileName = action.payload.name.slice(0, action.payload.name.lastIndexOf('.'))
    // Set Pinterest and Youtube title to video file name if empty
    if (state.posts.pinterest.title.trim() === '') {
      state.posts.pinterest.title = fileName
    }
    if (state.posts.youtube.title.trim() === '') {
      state.posts.youtube.title = fileName
    }
  },
  addImages: (state: Draft<ComposerState>, action: PayloadAction<Array<MediaItemState & Partial<FileUploadState>>>) => {
    const existingUrls = Object.values(state.images).reduce((acc, item) => {
      if (item.url) {
        acc[item.url] = true
      }
      return acc
    }, {} as Record<string, boolean>)
    const currentCount = Object.keys(state.images).length
    let index = 0
    action.payload.forEach((item) => {
      if (item.url && existingUrls[item.url]) {
        return
      }
      state.images[item.id] = {
        ...item,
        order: currentCount + index + 1,
        size: item.file?.size
      }
      index++
    })

    state.video = undefined
    state.contentItem = undefined
    state.links = []
    state.posts.facebook.textBackground = undefined
  },
  removeImages: (state: Draft<ComposerState>, action: PayloadAction<string[]>) => {
    action.payload.forEach(id => {
      const item = state.images[id]
      if (item?.url === (state.contentItem as Photo)?.imageUrl) {
        state.contentItem = undefined
      }
      delete state.images[id]
    })

    // update order of remaining images
    const remainingImages = Object.values(state.images).sort(sortByKeyAscending('order'))
    remainingImages.forEach((img, idx) => {
      img.order = idx + 1
    })
  },
  reorderImages: (state: Draft<ComposerState>, action: PayloadAction<string[]>) => {
    action.payload.forEach((id, index) => {
      const item = state.images[id]
      if (item) {
        item.order = index + 1
      }
    })
  },
  updateImage: (state: Draft<ComposerState>, action: PayloadAction<{ id: string } & Partial<MediaItemState>>) => {
    const { id, ...rest } = action.payload
    if (state.images[id]) {
      state.images[id] = { ...state.images[id], ...rest }

      if ((action.payload as any).isUploading) {
        state.links = []
        state.contentItem = undefined
      }
    }
  },
  addVideo: (state: Draft<ComposerState>, action: PayloadAction<{ id: string, url?: string, name?: string, isEdited?: boolean }>) => {
    const { id, name, url, isEdited } = action.payload
    state.video = { id, url, isEdited }
    state.images = {}
    state.contentItem = undefined
    state.links = []
    state.posts.facebook.textBackground = undefined
    state.posts.instagram.coverUrl = undefined
    const postType = state.posts.facebook.lastUsedVideoPostType === 'post' ? undefined : state.posts.facebook.lastUsedVideoPostType
    state.posts.facebook.postType = postType // default to last used video post type

    if (name) {
      if (state.posts.pinterest.title.trim() === '') {
        state.posts.pinterest.title = name
      }
      if (state.posts.youtube.title.trim() === '') {
        state.posts.youtube.title = name
      }
    }
  },
  removeVideo: (state: Draft<ComposerState>) => {
    state.video = undefined
    state.posts.instagram.coverUrl = undefined
  },
  updateVideo: (state: Draft<ComposerState>, action: PayloadAction<Partial<ComposerVideoState>>) => {
    if (state.video) {
      state.video = { ...state.video, ...action.payload }
    }
  }
}

export const addComposerImages = createAction<string[]>('composer/addImages')

export const mediaExtraReducers = (builder: ActionReducerMapBuilder<ComposerState>) => {
  builder
    .addCase(setComposerVideoMetadata.fulfilled, (state, action) => {
      const { duration, metadata, size } = action.payload
      if (state.video) {
        state.video.duration = duration
        state.video.metadata = metadata ? { ...state.video.metadata, ...metadata } : undefined
        state.video.size = size
      }
    })
}

export const setComposerVideoMetadata = createAppAsyncThunk('composer/setVideoMetadata', async (url: string, { rejectWithValue }) => {
  try {
    const duration = await getVideoDuration(url)
    const dimensions = getMediaDimensions('video', '[data-testid="composer-media"] video')
    const fileType = url.split('?')[0].substring(url.lastIndexOf('.') + 1).toLowerCase()
    const metadata: any = dimensions ? { ...dimensions } : {}
    metadata.fileType = fileType
    const response = await fetch(url, { method: 'head' })
    const size = response.headers.get('content-length')

    return {
      duration,
      metadata,
      size: size ? parseInt(size, 10) : undefined
    }
  } catch (e) {
    console.log('Could not get video metadata', e)
    return rejectWithValue(e)
  }
})

export function uploadFile(file: File, progressSubscriber?: PartialObserver<ProgressEvent>) {
  return (dispatch: StoreThunkDispatch) => dispatch(V1.uploadFile(file, progressSubscriber))
}

export function uploadPreview(file: File, videoUrl: string) {
  return (dispatch: StoreThunkDispatch) => dispatch(V1.uploadPreview(file, videoUrl))
}

export function deleteFile(url: string) {
  return (dispatch: StoreThunkDispatch) => dispatch(V1.deleteFile(url))
}
